
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createPost, getPostById, Post, updatePost } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";

interface PostFormProps {
  mode: "create" | "edit";
}

const PostForm = ({ mode }: PostFormProps) => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<Partial<Post>>({
    title: "",
    slug: "",
    content: "",
    thumbnail: "",
    excerpt: "",
  });
  const [isLoading, setIsLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchPost = async () => {
      if (mode === "edit" && id) {
        const data = await getPostById(parseInt(id));
        if (data) {
          setPost(data);
        } else {
          toast({
            title: "Post not found",
            description: "The post you are trying to edit does not exist.",
            variant: "destructive",
          });
          navigate("/admin/dashboard");
        }
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [mode, id, navigate, toast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPost({ ...post, [name]: value });
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-");
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setPost({
      ...post,
      title,
      slug: post.slug === "" ? generateSlug(title) : post.slug,
    });
  };

  const handleContentChange = (content: string) => {
    setPost({ ...post, content });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!post.title || !post.slug || !post.content) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    
    try {
      if (mode === "create") {
        const newPost = await createPost({
          ...post,
          author_id: user?.id || "anonymous",
        } as Omit<Post, "id" | "created_at" | "updated_at">);
        
        if (newPost) {
          toast({
            title: "Post created successfully",
          });
          navigate("/admin/dashboard");
        } else {
          toast({
            title: "Failed to create post",
            description: "Please try again.",
            variant: "destructive",
          });
        }
      } else if (mode === "edit" && id) {
        const updatedPost = await updatePost(parseInt(id), post);
        
        if (updatedPost) {
          toast({
            title: "Post updated successfully",
          });
          navigate("/admin/dashboard");
        } else {
          toast({
            title: "Failed to update post",
            description: "Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error saving post:", error);
      toast({
        title: "Error saving post",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-8">
      <h1 className="mb-8 text-3xl font-bold">
        {mode === "create" ? "Create New Post" : "Edit Post"}
      </h1>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            name="title"
            value={post.title}
            onChange={handleTitleChange}
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="slug">Slug *</Label>
          <Input
            id="slug"
            name="slug"
            value={post.slug}
            onChange={handleChange}
            required
          />
          <p className="text-sm text-muted-foreground">
            The URL-friendly version of the title. Will be auto-generated from the title if left empty.
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="thumbnail">Thumbnail URL</Label>
          <Input
            id="thumbnail"
            name="thumbnail"
            value={post.thumbnail}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
          />
          <p className="text-sm text-muted-foreground">
            URL to an image for the post thumbnail.
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="excerpt">Excerpt</Label>
          <Input
            id="excerpt"
            name="excerpt"
            value={post.excerpt}
            onChange={handleChange}
            placeholder="A short description of the post"
          />
          <p className="text-sm text-muted-foreground">
            A short summary that will be displayed on the homepage. If left empty, it will be generated from the content.
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="content">Content *</Label>
          <RichTextEditor content={post.content || ""} onChange={handleContentChange} />
        </div>
        
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/dashboard")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></span>
                Saving...
              </>
            ) : (
              mode === "create" ? "Create Post" : "Update Post"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PostForm;
